// signatures.js
// Loading and drawing technician signatures for the PDF report generators.
//
// This exists because signatures used to be looked up by DISPLAY NAME. Seven
// call sites each built their own `map[tech_name] = ...`, and the generators
// looked names up in it. Nothing kept those names in sync with the user record,
// so a miss was easy — and a miss fell through to `|| SIG_B64`, stamping the
// company owner's signature onto a report bearing someone else's name. On a
// compliance record that is a false attestation, not a cosmetic bug.
//
// Everything here is keyed by USER ID, which both the signature and the
// assignment already carry.
import axios from "axios";
import { API } from "./constants";
import { loadPhotoDataURL, drawPhoto } from "./pdfPhotos";

// Fetch every stored signature, keyed by the technician's user id.
//
// Values are complete data URLs. The call sites this replaces each re-encoded
// through a canvas and stripped the prefix, which forced every generator to
// hand-prepend "data:image/png;base64," and pinned them all to PNG.
//
// Never throws: an unsigned report is far better than no report, and this fetch
// previously being able to fail took the whole auto-file down with it.
export async function loadTechSignatures() {
  try {
    const res = await axios.get(`${API}/tech-signatures/`);
    const map = {};
    for (const s of res.data || []) {
      if (s.tech == null || !s.signature_url) continue;
      const dataUrl = await loadPhotoDataURL(s.signature_url);
      if (dataUrl) map[s.tech] = dataUrl;
    }
    return map;
  } catch (e) {
    console.warn("Could not load tech signatures", e);
    return {};
  }
}

// Draw a technician's signature above its line.
//
// Draws NOTHING when the signature is missing, which is the whole point: the
// line stays blank for a wet signature rather than being filled with whoever
// happened to be the fallback. Callers get the blank-line behaviour by default
// instead of having to remember it.
//
// Aspect ratio is preserved. The generators size these boxes with a hard-coded
// `width * (211 / 500)` — the proportions of the owner's own signature image —
// so an upload of any other shape used to be stretched to match it.
export function drawSignature(doc, dataUrl, x, y, w, h) {
  if (!dataUrl) return null;
  return drawPhoto(doc, dataUrl, x, y, w, h, { border: false, valign: "top" });
}
